/**
 * Almacenamiento de archivos (PDFs, comprobantes) — config por tenant.
 *
 * Permite alternar entre filesystem local del servidor y Azure Blob Storage.
 * Importante: cambiar este toggle SOLO afecta archivos NUEVOS. Los archivos
 * ya guardados siguen leyéndose desde el provider donde fueron creados — no
 * hay que migrar nada al cambiar la config.
 */
import { useState, useEffect } from 'react';
import { storageApi } from '../../utils/api';
import { HardDrive, Cloud, Eye, EyeOff, Loader2, Check, AlertCircle, RefreshCw, Save, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

export default function AlmacenamientoTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState({
    provider: 'local',
    azureAccountName: '',
    azureContainer: '',
    pathPrefix: '',
    hasConnectionString: false
  });
  const [connectionString, setConnectionString] = useState(''); // sólo para nuevo input
  const [showCs, setShowCs] = useState(false);
  const [stats, setStats] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    load();
    loadStats();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await storageApi.get();
      setConfig(res.data.data);
    } catch (e) {
      toast.error('No se pudo cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await storageApi.stats();
      setStats(res.data.data);
    } catch (e) { /* no rompe la pantalla */ }
  };

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      const payload = {
        provider: config.provider,
        azureAccountName: config.azureAccountName,
        azureContainer: config.azureContainer,
        pathPrefix: config.pathPrefix
      };
      // Solo enviar connection string si el user lo modificó (no enviar vacío para preservar el actual)
      if (connectionString.trim() !== '') payload.azureConnectionString = connectionString.trim();
      await storageApi.update(payload);
      toast.success('Configuración guardada');
      setConnectionString(''); // limpiar el input por seguridad
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await storageApi.test();
      setTestResult({ ok: true, msg: `Conexión exitosa con ${res.data.data.accountName}/${res.data.data.container}` });
      toast.success('La conexión con Azure funciona');
    } catch (e) {
      setTestResult({ ok: false, msg: e.response?.data?.error || 'Error al probar conexión' });
      toast.error('La prueba de conexión falló');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-violet-600" size={32} />
      </div>
    );
  }

  const isAzure = config.provider === 'azure_blob';

  const tarjeta = 'bg-white rounded-lg border-2 border-gray-200 p-5';
  const campo = 'w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-violet-500 focus:border-violet-500';
  const opcion = (activo) => `p-4 rounded-lg border-2 text-left transition ${activo ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-gray-300'}`;
  const ayuda = 'text-[11px] text-gray-500 mt-1';

  // 27/10/2026: arriba dos columnas del mismo alto (aviso y proveedor | archivos guardados); abajo los datos de Azure a
  // todo el ancho. Antes era una sola columna angosta, con textos en inglés.
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
        <div className="flex flex-col gap-4 min-w-0">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Cambiar el almacenamiento no mueve los archivos que ya existen.</p>
              <p className="text-blue-800 text-[13px] leading-relaxed">
                Cada archivo recuerda dónde se guardó. Si activa Azure hoy, los archivos anteriores se siguen leyendo desde el
                disco del servidor y los nuevos se guardan en Azure. Si después vuelve al disco del servidor, los archivos de Azure
                siguen funcionando: solo cambia dónde se guardan los próximos.
              </p>
            </div>
          </div>

          <div className={`${tarjeta} flex-1`}>
            <h3 className="text-base font-semibold text-gray-900 mb-4">Dónde se guardan los archivos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button type="button" onClick={() => setConfig({ ...config, provider: 'local' })} className={opcion(config.provider === 'local')}>
                <div className="flex items-center gap-3 mb-2">
                  <HardDrive className={`h-6 w-6 ${config.provider === 'local' ? 'text-violet-600' : 'text-gray-400'}`} />
                  <span className="font-semibold text-gray-900">Disco del servidor</span>
                  {config.provider === 'local' && <Check className="h-4 w-4 ml-auto text-violet-600" />}
                </div>
                <p className="text-xs text-gray-600">En el disco del servidor de Finance 360. Sin costo adicional, pero limitado al espacio de ese disco.</p>
              </button>
              <button type="button" onClick={() => setConfig({ ...config, provider: 'azure_blob' })} className={opcion(config.provider === 'azure_blob')}>
                <div className="flex items-center gap-3 mb-2">
                  <Cloud className={`h-6 w-6 ${config.provider === 'azure_blob' ? 'text-violet-600' : 'text-gray-400'}`} />
                  <span className="font-semibold text-gray-900">Azure (nube)</span>
                  {config.provider === 'azure_blob' && <Check className="h-4 w-4 ml-auto text-violet-600" />}
                </div>
                <p className="text-xs text-gray-600">En la nube de Azure: copias en varias regiones, crece sin límite y borra lo viejo según reglas.</p>
              </button>
            </div>
          </div>
        </div>

        <div className={`${tarjeta} min-w-0`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-600" />
              Archivos guardados hoy
            </h3>
            <button onClick={loadStats} className="text-xs text-violet-600 hover:underline flex items-center gap-1">
              <RefreshCw className="h-3 w-3" /> Actualizar
            </button>
          </div>
          {!stats ? (
            <p className="text-sm text-gray-500">No se pudo leer la cantidad de archivos.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <HardDrive className="h-4 w-4 text-gray-500" />
                    <span className="text-xs text-gray-600 uppercase tracking-wider">Disco del servidor</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums">{stats.local || 0}</p>
                  <p className="text-xs text-gray-500">archivos</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Cloud className="h-4 w-4 text-blue-500" />
                    <span className="text-xs text-blue-600 uppercase tracking-wider">Azure</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900 tabular-nums">{stats.azure_blob || 0}</p>
                  <p className="text-xs text-blue-700">archivos</p>
                </div>
              </div>
              {stats.by_table && Object.keys(stats.by_table).length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Por tipo de archivo</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500">
                        <th className="text-left font-normal pb-1">Tabla</th>
                        <th className="text-right font-normal pb-1">Disco</th>
                        <th className="text-right font-normal pb-1">Azure</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(stats.by_table).map(([t, counts]) => (
                        <tr key={t} className="border-t border-gray-100">
                          <td className="py-1.5 font-mono text-xs text-gray-700">{t}</td>
                          <td className="py-1.5 text-right tabular-nums">{counts.local || 0}</td>
                          <td className="py-1.5 text-right text-blue-700 tabular-nums">{counts.azure_blob || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isAzure ? (
        <div className={`${tarjeta} space-y-4`}>
          <h3 className="text-base font-semibold text-gray-900">Datos de la cuenta de Azure</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cuenta de almacenamiento <span className="text-red-500">*</span></label>
              <input type="text" value={config.azureAccountName || ''} onChange={(e) => setConfig({ ...config, azureAccountName: e.target.value })} placeholder="finance360storage" className={campo} />
              <p className={ayuda}>El nombre de la cuenta (Storage Account), sin «.blob.core.windows.net».</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contenedor <span className="text-red-500">*</span></label>
              <input type="text" value={config.azureContainer || ''} onChange={(e) => setConfig({ ...config, azureContainer: e.target.value })} placeholder="finance360-docs" className={campo} />
              <p className={ayuda}>Tiene que existir y ser privado (sin acceso público).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Carpeta dentro del contenedor <span className="text-gray-400 font-normal">(opcional)</span></label>
              <input type="text" value={config.pathPrefix || ''} onChange={(e) => setConfig({ ...config, pathPrefix: e.target.value })} placeholder="tenant1/" className={campo} />
              <p className={ayuda}>Se antepone a cada archivo. Sirve si el contenedor lo comparten varias cuentas.</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cadena de conexión <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type={showCs ? 'text' : 'password'}
                value={connectionString}
                onChange={(e) => setConnectionString(e.target.value)}
                placeholder={config.hasConnectionString ? '••••••• (guardada; deje vacío para conservarla)' : 'DefaultEndpointsProtocol=https;AccountName=...;AccountKey=...;EndpointSuffix=core.windows.net'}
                className={`${campo} pr-10 font-mono text-xs`}
              />
              <button type="button" onClick={() => setShowCs(!showCs)} className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600" tabIndex={-1} aria-label={showCs ? 'Ocultar' : 'Mostrar'}>
                {showCs ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className={ayuda}>
              {config.hasConnectionString
                ? 'Ya hay una guardada. Deje el campo vacío para conservarla o escriba una nueva para reemplazarla.'
                : 'Se copia en el portal de Azure: la cuenta de almacenamiento → Claves de acceso (Access keys).'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={handleTest} disabled={testing || saving}
              className="px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 font-medium flex items-center gap-2">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Probar conexión
            </button>
            {testResult && (
              <span className={`text-sm flex items-center gap-1.5 ${testResult.ok ? 'text-emerald-700' : 'text-red-700'}`}>
                {testResult.ok ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                {testResult.msg}
              </span>
            )}
            <p className="text-[11px] text-gray-500">
              La prueba sube y borra un archivo temporal en <code>_health/</code> para revisar la cuenta, los permisos y el contenedor. <strong>Guarde los cambios antes de probar.</strong>
            </p>
          </div>
        </div>
      ) : (
        <div className={`${tarjeta} text-sm text-gray-600`}>
          Los archivos nuevos se guardan en el disco del servidor de Finance 360. No hace falta configurar nada más.
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button type="button" onClick={load} disabled={saving} className="px-5 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
          Descartar cambios
        </button>
        <button type="button" onClick={handleSave} disabled={saving}
          className="px-6 py-2 border-2 border-violet-500 text-violet-600 rounded-lg hover:bg-violet-50 disabled:opacity-50 font-medium flex items-center gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar configuración
        </button>
      </div>
    </div>
  );
}
